/**
 * Cleans up form data by removing empty nested objects and preserving non-empty values.
 *
 * This function processes form data to improve its structure before submission:
 * - For nested objects: If all properties are empty (empty strings, empty arrays, null, undefined),
 *   the object is replaced with an empty object `{}`.
 * - If any property within a nested object has a value, the entire object is preserved as-is.
 * - Non-object values (like arrays, strings, numbers) are kept unchanged.
 * - Null or undefined values are removed entirely.
 *
 * @param {Record<string, unknown>} data - The original form data to be cleaned
 * @returns {Record<string, unknown>} The cleaned data with empty nested objects simplified
 *
 * @example
 * // Input
 * const formData = {
 *   "filter1": [],
 *   "filter2": {
 *     "level1": "",
 *     "level2": "",
 *     "level3": []
 *   },
 *   "filter3": {
 *     "level1": "value",
 *     "level2": "",
 *     "level3": []
 *   }
 * };
 *
 * // Output
 * // {
 * //   "filter1": [],
 * //   "filter2": {},
 * //   "filter3": {
 * //     "level1": "value",
 * //     "level2": "",
 * //     "level3": []
 * //   }
 * // }
 * const cleanedData = cleanupFormData(formData);
 */
export const cleanupFormData = (data: Record<string, unknown>): Record<string, unknown> => {
  const cleanedData: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined) {
      continue
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      // Check if any nested property has a non-empty value
      const hasNonEmptyValue = Object.values(value).some(
        (v) => v !== null && v !== undefined && v !== '' && (Array.isArray(v) ? v.length > 0 : true)
      )

      if (hasNonEmptyValue) {
        // If any nested property has a value, include all properties
        cleanedData[key] = value
      } else {
        // If all nested properties are empty, set to empty object
        cleanedData[key] = {}
      }
    } else {
      // For non-object values, keep them as is
      cleanedData[key] = value
    }
  }

  return cleanedData
}
